import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const B = {
  gold: "#D4A843", green: "#43B581", red: "#E74C3C", orange: "#FF6B35",
  blue: "#5DADE2", bg: "#0D0F13", card: "#13161C", border: "#1E222A",
  white: "#F0F0F0", whiteGray: "#A0A8B8", gray: "#6B7280",
};

const PERM_DESCRIPTIONS = {
  users: "User Management — grant/revoke course access, delete users, revoke sessions",
  progress: "Progress Viewer — read-only student progress + capstone state",
  notifications: "Notifications — view access requests, send push test broadcasts",
  cohort: "Cohort Challenges — create / edit / delete weekly cohort challenges",
  analytics: "Analytics — overview dashboard + quiz stats + AXEL nudge analytics",
};

function authHeaders() {
  const token = localStorage.getItem("flexx_jwt");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);  // { id, email, perms: Set }
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoteSearchResult, setPromoteSearchResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // Autocomplete state — single prefetch of the user list on focus,
  // filtered client-side as the admin types.
  const [allUsers, setAllUsers] = useState(null);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadAllUsersOnce = useCallback(async () => {
    if (allUsers || allUsersLoading) return;
    setAllUsersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { ...ax, headers: authHeaders() });
      const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
      setAllUsers(list);
    } catch {
      setAllUsers([]);
    } finally {
      setAllUsersLoading(false);
    }
  }, [allUsers, allUsersLoading]);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/admins`, { ...ax, headers: authHeaders() });
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const promoteUser = async (userId, perms) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/admins/${userId}/promote`,
        { permissions: Array.from(perms) },
        { ...ax, headers: authHeaders() },
      );
      setPromoteEmail("");
      setPromoteSearchResult(null);
      await fetchAdmins();
    } catch (e) {
      alert(e?.response?.data?.detail || "Promote failed");
    } finally { setBusy(false); }
  };

  const updatePerms = async (userId, perms) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/admins/${userId}/permissions`,
        { permissions: Array.from(perms) },
        { ...ax, headers: authHeaders() },
      );
      setEditing(null);
      await fetchAdmins();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const revokeAdmin = async (userId, email) => {
    if (!window.confirm(`Revoke admin access for ${email}?\n\nThey'll be demoted back to a regular student. Their account stays intact.`)) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/admins/${userId}/revoke`,
        {},
        { ...ax, headers: authHeaders() },
      );
      await fetchAdmins();
    } catch (e) {
      alert(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div data-testid="admins-loading" style={{ padding: 24, color: B.whiteGray }}>Loading admins…</div>;
  if (err) return <div data-testid="admins-error" style={{ padding: 24, color: B.red }}>{err}</div>;
  if (!data) return null;

  return (
    <div data-testid="admins-panel" style={{ padding: "16px 24px 40px" }}>
      <div style={{ background: `${B.gold}10`, border: `1px solid ${B.gold}40`, borderLeft: `4px solid ${B.gold}`, borderRadius: 10, padding: "14px 18px", marginBottom: 24 }}>
        <div style={{ color: B.gold, fontSize: 12, fontWeight: 800, letterSpacing: 1.2, marginBottom: 4 }}>● ADMIN ROLES</div>
        <div style={{ color: B.whiteGray, fontSize: 13 }}>
          Owner: <strong style={{ color: B.gold }} data-testid="owner-email">{data.owner_email}</strong> · {data.admins.length} admin{data.admins.length === 1 ? "" : "s"} total
        </div>
      </div>

      {/* Promote new admin */}
      <div style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 24 }}>
        <div style={{ color: B.gold, fontSize: 13, fontWeight: 800, letterSpacing: 1.2, marginBottom: 12, textTransform: "uppercase" }}>
          Promote a User to Sub-Admin
        </div>
        <UserAutocomplete
          value={promoteEmail}
          onChange={(v) => { setPromoteEmail(v); setPromoteSearchResult(null); }}
          users={allUsers || []}
          loading={allUsersLoading}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          onFocus={loadAllUsersOnce}
          onPick={(u) => {
            setPromoteEmail(u.email);
            setShowDropdown(false);
            setPromoteSearchResult({ user: { ...u, id: u.id || u._id } });
          }}
          selectedEmail={promoteSearchResult?.user?.email}
        />
        {promoteSearchResult?.error && (
          <div data-testid="promote-search-error" style={{ marginTop: 10, color: B.red, fontSize: 12 }}>{promoteSearchResult.error}</div>
        )}
        {promoteSearchResult?.user && (
          <PermissionEditor
            label={`Promote ${promoteSearchResult.user.email}`}
            initialPerms={new Set(["users", "progress"])}
            available={data.available_permissions}
            onSubmit={(perms) => promoteUser(promoteSearchResult.user.id, perms)}
            onCancel={() => setPromoteSearchResult(null)}
            submitLabel="Promote to Sub-Admin"
            busy={busy}
          />
        )}
      </div>

      {/* Existing admins */}
      <div style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: 12, padding: "16px 20px" }}>
        <div style={{ color: B.gold, fontSize: 13, fontWeight: 800, letterSpacing: 1.2, marginBottom: 12, textTransform: "uppercase" }}>
          Current Admins ({data.admins.length})
        </div>
        <table data-testid="admins-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: B.gray, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>
              <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Admin</th>
              <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Permissions</th>
              <th style={{ textAlign: "right", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.admins.map((a) => (
              <tr key={a.id} data-testid={`admin-row-${a.id}`} style={{ color: B.white }}>
                <td style={{ padding: "12px 6px", borderBottom: `1px solid ${B.border}`, verticalAlign: "top" }}>
                  <div style={{ color: B.white, fontWeight: 600 }}>
                    {a.name || "—"}
                    {a.is_owner && <span style={{ marginLeft: 8, fontSize: 10, color: B.gold, fontWeight: 800, letterSpacing: 1 }}>● OWNER</span>}
                  </div>
                  <div style={{ color: B.gray, fontSize: 11, fontFamily: "monospace" }}>{a.email}</div>
                </td>
                <td style={{ padding: "12px 6px", borderBottom: `1px solid ${B.border}`, verticalAlign: "top" }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {a.admin_permissions.map((p) => (
                      <span key={p} style={{ background: `${B.blue}20`, color: B.blue, padding: "2px 8px", borderRadius: 50, fontSize: 11, fontWeight: 600 }}>{p}</span>
                    ))}
                    {a.admin_permissions.length === 0 && <span style={{ color: B.gray, fontSize: 11 }}>(no permissions)</span>}
                  </div>
                </td>
                <td style={{ padding: "12px 6px", borderBottom: `1px solid ${B.border}`, textAlign: "right", verticalAlign: "top" }}>
                  {a.is_owner ? (
                    <span style={{ color: B.gray, fontSize: 11, fontStyle: "italic" }}>locked</span>
                  ) : (
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button
                        data-testid={`edit-perms-${a.id}`}
                        onClick={() => setEditing({ id: a.id, email: a.email, perms: new Set(a.admin_permissions) })}
                        style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${B.border}`, background: "transparent", color: B.whiteGray, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                      >Edit</button>
                      <button
                        data-testid={`revoke-admin-${a.id}`}
                        onClick={() => revokeAdmin(a.id, a.email)}
                        disabled={busy}
                        style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${B.red}40`, background: "transparent", color: B.red, fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: busy ? 0.6 : 1 }}
                      >Revoke</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit-perms inline panel */}
      {editing && (
        <div style={{ background: B.card, border: `1px solid ${B.gold}40`, borderRadius: 12, padding: "16px 20px", marginTop: 20 }}>
          <PermissionEditor
            label={`Edit permissions for ${editing.email}`}
            initialPerms={editing.perms}
            available={data.available_permissions}
            onSubmit={(perms) => updatePerms(editing.id, perms)}
            onCancel={() => setEditing(null)}
            submitLabel="Save Permissions"
            busy={busy}
          />
        </div>
      )}
    </div>
  );
}

function UserAutocomplete({ value, onChange, users, loading, showDropdown, setShowDropdown, onFocus, onPick, selectedEmail }) {
  // Filter users by email OR name substring. Admins are excluded from
  // suggestions (they're already admins — show them in the table below).
  const q = (value || "").toLowerCase().trim();
  const matches = (users || [])
    .filter((u) => u.role !== "admin")
    .filter((u) => {
      if (!q) return true;
      const email = (u.email || "").toLowerCase();
      const name = (u.name || "").toLowerCase();
      return email.includes(q) || name.includes(q);
    })
    .slice(0, 8);

  const initials = (u) => {
    const src = (u.name || u.email || "?").trim();
    return (src[0] || "?").toUpperCase();
  };

  return (
    <div style={{ position: "relative" }} data-testid="promote-autocomplete">
      <input
        data-testid="promote-email-input"
        value={value}
        onChange={(e) => { onChange(e.target.value); setShowDropdown(true); }}
        onFocus={() => { onFocus?.(); setShowDropdown(true); }}
        onBlur={() => { setTimeout(() => setShowDropdown(false), 150); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setShowDropdown(false);
          if (e.key === "Enter" && matches.length > 0) {
            e.preventDefault();
            onPick(matches[0]);
          }
        }}
        placeholder={loading ? "Loading users..." : "Search by name or email…"}
        autoComplete="off"
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 8,
          border: `1px solid ${selectedEmail === value ? B.green : B.border}`,
          background: B.bg, color: B.white, fontSize: 13, outline: "none",
          boxSizing: "border-box",
        }}
      />
      {showDropdown && matches.length > 0 && (
        <div data-testid="promote-autocomplete-dropdown" style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: B.card, border: `1px solid ${B.border}`, borderRadius: 8,
          maxHeight: 320, overflowY: "auto", zIndex: 50,
          boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
        }}>
          {matches.map((u) => (
            <div
              key={u.id || u._id}
              data-testid={`promote-suggestion-${u.id || u._id}`}
              onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                cursor: "pointer", borderBottom: `1px solid ${B.border}`,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${B.gold}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: `linear-gradient(135deg, ${B.gold} 0%, ${B.orange} 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: B.bg, fontSize: 14, fontWeight: 800,
              }}>{initials(u)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: B.white, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.name || "(no name)"}
                </div>
                <div style={{ color: B.gray, fontSize: 11, fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.email}
                </div>
              </div>
              {u.course_access?.length > 0 && (
                <span style={{ background: `${B.blue}20`, color: B.blue, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 50 }}>
                  {u.course_access.length} access
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {showDropdown && !loading && q && matches.length === 0 && (
        <div data-testid="promote-autocomplete-empty" style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: B.card, border: `1px solid ${B.border}`, borderRadius: 8,
          padding: "12px 14px", color: B.gray, fontSize: 12, zIndex: 50,
        }}>
          No registered users matching "{value}".
        </div>
      )}
    </div>
  );
}

function PermissionEditor({ label, initialPerms, available, onSubmit, onCancel, submitLabel, busy }) {
  const [perms, setPerms] = useState(new Set(initialPerms));

  const toggle = (p) => {
    const next = new Set(perms);
    if (next.has(p)) next.delete(p); else next.add(p);
    setPerms(next);
  };

  return (
    <div data-testid="permission-editor" style={{ marginTop: 14 }}>
      <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {available.map((p) => (
          <label key={p} data-testid={`perm-${p}`} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 10px", borderRadius: 6, background: perms.has(p) ? `${B.green}10` : "transparent", border: `1px solid ${perms.has(p) ? `${B.green}40` : B.border}` }}>
            <input
              type="checkbox"
              data-testid={`perm-check-${p}`}
              checked={perms.has(p)}
              onChange={() => toggle(p)}
              style={{ marginTop: 2, accentColor: B.green }}
            />
            <span>
              <span style={{ color: B.white, fontSize: 13, fontWeight: 700 }}>{p}</span>
              <span style={{ color: B.whiteGray, fontSize: 11, marginLeft: 8 }}>{PERM_DESCRIPTIONS[p] || ""}</span>
            </span>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          data-testid="permission-editor-submit"
          onClick={() => onSubmit(perms)}
          disabled={busy}
          style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: B.green, color: B.bg, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: busy ? 0.6 : 1 }}
        >{submitLabel}</button>
        <button
          data-testid="permission-editor-cancel"
          onClick={onCancel}
          disabled={busy}
          style={{ padding: "10px 18px", borderRadius: 8, border: `1px solid ${B.border}`, background: "transparent", color: B.whiteGray, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >Cancel</button>
      </div>
    </div>
  );
}
